const noticeSelector = '[data-international-tax-notice]';
const closeSelector = '[data-international-tax-notice-close]';
const nativeBannerSelector = '#shopify-pc__banner';
const preferencesSelector = '#shopify-pc__prefs';
const dismissalDuration = 30 * 24 * 60 * 60 * 1000;
const exitDuration = 200;

const prefersReducedMotion = () =>
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const hasValidDismissal = (storageKey, now = Date.now()) => {
  if (!storageKey) return false;

  try {
    const expiresAt = Number.parseInt(window.localStorage.getItem(storageKey) || '', 10);
    return Number.isFinite(expiresAt) && expiresAt > now;
  } catch (error) {
    return false;
  }
};

const persistDismissal = (storageKey, now = Date.now()) => {
  if (!storageKey) return false;

  try {
    window.localStorage.setItem(storageKey, String(now + dismissalDuration));
    return true;
  } catch (error) {
    return false;
  }
};

const isSurfaceVisible = (surface) => {
  if (!surface || surface.hidden || surface.getAttribute?.('aria-hidden') === 'true') {
    return false;
  }

  try {
    const styles = window.getComputedStyle(surface);
    return styles.display !== 'none' && styles.visibility !== 'hidden';
  } catch (error) {
    return false;
  }
};

const setPrivacyBlocked = (notice, blocked) => {
  if (blocked) {
    if (notice.dataset.internationalTaxNoticePrivacyState !== 'blocked') {
      notice.dataset.internationalTaxNoticePrivacyState = 'blocked';
    }
    if (notice.getAttribute('aria-hidden') !== 'true') {
      notice.setAttribute('aria-hidden', 'true');
    }
    if (!notice.hasAttribute('inert')) {
      notice.setAttribute('inert', '');
    }
    return;
  }

  if (notice.dataset.internationalTaxNoticePrivacyState === 'blocked') {
    delete notice.dataset.internationalTaxNoticePrivacyState;
  }

  if (notice.dataset.internationalTaxNoticeState === 'closing') {
    return;
  }

  if (notice.getAttribute('aria-hidden') !== null) {
    notice.removeAttribute('aria-hidden');
  }
  if (notice.hasAttribute('inert')) {
    notice.removeAttribute('inert');
  }
};

const observePrivacySurfaces = (notice) => {
  const privacySelector = `${nativeBannerSelector}, ${preferencesSelector}`;
  let surfaces = [];
  const sync = () => {
    surfaces = [document.querySelector(nativeBannerSelector), ...document.querySelectorAll(preferencesSelector)].filter(Boolean);
    try {
      setPrivacyBlocked(notice, surfaces.some(isSurfaceVisible));
    } catch (error) {
      setPrivacyBlocked(notice, false);
    }
  };

  sync();

  if (typeof MutationObserver !== 'function' || !document.body) {
    setPrivacyBlocked(notice, false);
    return null;
  }

  try {
    // Product/carousel animations also mutate styles. Only privacy surfaces or
    // their ancestors can change whether the notice must yield to consent UI.
    const containsPrivacy = (node) => Boolean(node?.matches?.(privacySelector)
      || node?.querySelector?.(privacySelector)
      || surfaces.some(surface => node === surface || node?.contains?.(surface)));
    const observer = new MutationObserver((records) => {
      if (!records || records.some(record => record.type === 'attributes'
        ? surfaces.some(surface => record.target === surface || record.target?.contains?.(surface))
        : [...(record.addedNodes || []), ...(record.removedNodes || [])].some(containsPrivacy))) sync();
    });
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: [
        'hidden',
        'style',
        'class',
        'aria-hidden',
        'data-sigma-cookie-ready',
        'data-sigma-cookie-state',
      ],
    });
    return observer;
  } catch (error) {
    setPrivacyBlocked(notice, false);
    return null;
  }
};

const finishDismissal = (notice) => {
  notice.hidden = true;
  delete notice.dataset.internationalTaxNoticeState;
  notice.removeAttribute('aria-hidden');
  notice.removeAttribute('inert');
  if ('inert' in notice) notice.inert = false;
};

const dismissNotice = (notice) => {
  const storageKey = notice.dataset.internationalTaxNoticeStorageKey;
  persistDismissal(storageKey);

  if (prefersReducedMotion()) {
    finishDismissal(notice);
    return;
  }

  notice.dataset.internationalTaxNoticeState = 'closing';
  notice.setAttribute('aria-hidden', 'true');
  notice.setAttribute('inert', '');
  if ('inert' in notice) notice.inert = true;

  let fallbackTimeout;
  let finished = false;
  const finish = () => {
    if (finished) return;

    finished = true;
    window.clearTimeout(fallbackTimeout);
    notice.removeEventListener('animationend', handleAnimationEnd);
    finishDismissal(notice);
  };
  const handleAnimationEnd = (event) => {
    if (event.animationName !== 'international-tax-notice-out') return;
    finish();
  };

  notice.addEventListener('animationend', handleAnimationEnd);
  fallbackTimeout = window.setTimeout(finish, exitDuration + 100);
};

const initializeNotice = () => {
  const notice = document.querySelector(noticeSelector);
  if (!notice) return;

  const storageKey = notice.dataset.internationalTaxNoticeStorageKey;
  if (hasValidDismissal(storageKey)) {
    notice.hidden = true;
  }

  const close = notice.querySelector(closeSelector);
  if (close) {
    close.addEventListener('click', () => dismissNotice(notice));
    close.hidden = false;
  }

  observePrivacySurfaces(notice);
};

initializeNotice();
